// Admin view of the random-sample potential-member survey (/survey + paper intake).
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, ExternalLink, RefreshCw, Target } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const SOURCE_LABEL = { online: "Online", paper: "Paper", church: "Church", rally: "Rally", event: "Event", phone: "Phone" };

function CountyBar({ name, count, target }) {
  const pct = Math.min(100, Math.round((count / target) * 100));
  return (
    <div data-testid={`admin-sample-county-${name.toLowerCase()}`}>
      <div className="flex justify-between text-xs text-bdu-cream/70">
        <span>{name} County</span>
        <span className="tabular-nums"><span className="text-bdu-gold">{count}</span> / {target}</span>
      </div>
      <div className="mt-2 h-1.5 bg-bdu-gold/10"><div className="h-full bg-bdu-gold" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function RecentList({ rows }) {
  if (!rows.length) return <p className="text-xs text-bdu-cream/45">No responses yet. Share the survey link to start the sample.</p>;
  return (
    <ul className="divide-y divide-bdu-gold/10 text-xs" data-testid="admin-sample-recent">
      {rows.map((s) => (
        <li key={s.id} className="py-2 flex flex-wrap justify-between gap-2 text-bdu-cream/70">
          <span className="tabular-nums">{s.zip} · {s.in_fom ? `${s.fom_county}` : "Outside FOM"}</span>
          <span>{SOURCE_LABEL[s.source] || s.source} · {s.likely_to_join} · {new Date(s.created_at).toLocaleDateString()}</span>
        </li>
      ))}
    </ul>
  );
}

export default function SampleProgressCard() {
  const [p, setP] = useState(null);
  const [recent, setRecent] = useState([]);
  const link = `${window.location.origin}/survey`;

  const load = () => {
    api.get("/survey/progress").then((r) => setP(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/admin/credit-union/surveys", { params: { limit: 8 } }).then((r) => setRecent(r.data.surveys)).catch(() => {});
  };
  useEffect(load, []);

  const copy = () => navigator.clipboard.writeText(link).then(() => toast.success("Survey link copied"), () => toast.error("Copy failed"));

  return (
    <section className="border border-bdu-gold/40 bg-bdu-gold/5 p-6 space-y-6" data-testid="admin-sample-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Target size={12} /> Random-sample member survey</div>
          <h3 className="mt-2 font-serif text-2xl text-bdu-cream" data-testid="admin-sample-headline">
            {p ? `${p.counted} of ${p.target} counted` : "Loading…"}
          </h3>
          {p && <p className="mt-1 text-xs text-bdu-cream/55">{p.confidence}% confidence · ±{p.margin}% margin · {p.responses_in_fom} in-FOM responses · {p.out_of_fom} outside FOM{p.quotas_met ? " · quotas met" : ""}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={load} className="btn-ghost text-xs inline-flex items-center gap-2" data-testid="admin-sample-refresh-btn"><RefreshCw size={13} /> Refresh</button>
          <button onClick={copy} className="btn-ghost text-xs inline-flex items-center gap-2" data-testid="admin-sample-copy-btn"><Copy size={13} /> Copy link</button>
          <a href="/survey" target="_blank" rel="noopener noreferrer" className="btn-gold text-xs" data-testid="admin-sample-open-btn"><ExternalLink size={13} /> Open survey</a>
        </div>
      </div>
      {p && (
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-4">
            {Object.entries(p.counties).map(([name, c]) => <CountyBar key={name} name={name} count={c.count} target={c.target} />)}
            <div className="flex flex-wrap gap-2 pt-1" data-testid="admin-sample-sources">
              {Object.entries(p.by_source).map(([k, n]) => (
                <span key={k} className="text-[10px] uppercase tracking-[0.2em] border border-bdu-gold/25 px-2 py-1 text-bdu-cream/70">{SOURCE_LABEL[k] || k} · {n}</span>
              ))}
            </div>
          </div>
          <div><div className="label-eyebrow mb-2" style={{ fontSize: "0.6rem" }}>Latest responses</div><RecentList rows={recent} /></div>
        </div>
      )}
    </section>
  );
}
